import type { CheckersGame, Piece } from "./types.js";
import { EMPTY, RED, BLACK, RED_KING, BLACK_KING, SIZE } from "./types.js";

export function createGame(): CheckersGame {
  const board: Piece[][] = Array.from({ length: SIZE }, () => Array(SIZE).fill(EMPTY));
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < SIZE; c++) {
      if ((r + c) % 2 === 1) board[r][c] = BLACK;
    }
  }
  for (let r = SIZE - 3; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if ((r + c) % 2 === 1) board[r][c] = RED;
    }
  }
  return {
    board, turn: 1, winner: 0, isDraw: false,
    mustJump: false, jumpPositions: [], selected: null, validMoves: [], multiJump: null,
  };
}

function isRed(p: Piece): boolean { return p === RED || p === RED_KING; }
function isBlack(p: Piece): boolean { return p === BLACK || p === BLACK_KING; }
function isKing(p: Piece): boolean { return p === RED_KING || p === BLACK_KING; }
function owner(p: Piece): 1 | 2 { return isRed(p) ? 1 : 2; }

function findAllJumps(board: Piece[][], player: 1 | 2): Array<{ r: number; c: number }> {
  const jumps: Array<{ r: number; c: number }> = [];
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (p === EMPTY) continue;
      if (owner(p) !== player) continue;
      if (hasJumpFrom(board, r, c).length > 0) jumps.push({ r, c });
    }
  }
  return jumps;
}

function hasJumpFrom(board: Piece[][], r: number, c: number): Array<{ r: number; c: number; captured: { r: number; c: number } }> {
  const p = board[r][c];
  if (p === EMPTY) return [];
  const dirs: Array<[number, number]> = [];
  if (isRed(p) || isKing(p)) { dirs.push([-1, -1], [-1, 1]); }
  if (isBlack(p) || isKing(p)) { dirs.push([1, -1], [1, 1]); }
  const jumps: Array<{ r: number; c: number; captured: { r: number; c: number } }> = [];
  for (const [dr, dc] of dirs) {
    const mr = r + dr, mc = c + dc;
    const lr = r + 2 * dr, lc = c + 2 * dc;
    if (lr < 0 || lr >= SIZE || lc < 0 || lc >= SIZE) continue;
    const mid = board[mr][mc];
    if (mid === EMPTY) continue;
    if (owner(mid) === owner(p)) continue;
    if (board[lr][lc] !== EMPTY) continue;
    jumps.push({ r: lr, c: lc, captured: { r: mr, c: mc } });
  }
  return jumps;
}

function findSimpleMoves(board: Piece[][], r: number, c: number): Array<{ r: number; c: number }> {
  const p = board[r][c];
  if (p === EMPTY) return [];
  const dirs: Array<[number, number]> = [];
  if (isRed(p) || isKing(p)) { dirs.push([-1, -1], [-1, 1]); }
  if (isBlack(p) || isKing(p)) { dirs.push([1, -1], [1, 1]); }
  const moves: Array<{ r: number; c: number }> = [];
  for (const [dr, dc] of dirs) {
    const nr = r + dr, nc = c + dc;
    if (nr < 0 || nr >= SIZE || nc < 0 || nc >= SIZE) continue;
    if (board[nr][nc] !== EMPTY) continue;
    moves.push({ r: nr, c: nc });
  }
  return moves;
}

export function getValidMoves(game: CheckersGame, r: number, c: number): Array<{ r: number; c: number; jump: boolean; captured?: { r: number; c: number } }> {
  const p = game.board[r][c];
  if (p === EMPTY) return [];
  if (owner(p) !== game.turn) return [];
  if (game.mustJump) {
    const jumps = hasJumpFrom(game.board, r, c);
    return jumps.map((j) => ({ r: j.r, c: j.c, jump: true, captured: j.captured }));
  }
  const simple = findSimpleMoves(game.board, r, c);
  return simple.map((m) => ({ r: m.r, c: m.c, jump: false }));
}

export function selectCell(game: CheckersGame, r: number, c: number): CheckersGame {
  if (game.winner !== 0 || game.isDraw) return game;
  const p = game.board[r][c];

  if (game.multiJump) {
    const jumps = hasJumpFrom(game.board, game.multiJump.r, game.multiJump.c);
    const move = jumps.find((j) => j.r === r && j.c === c);
    if (!move) return game;
    return applyMove(game, game.multiJump.r, game.multiJump.c, r, c, move.captured, true);
  }

  if (p !== EMPTY && owner(p) === game.turn) {
    const moves = getValidMoves(game, r, c);
    return { ...game, selected: { r, c }, validMoves: moves };
  }

  if (game.selected) {
    const move = game.validMoves.find((m) => m.r === r && m.c === c);
    if (move) {
      return applyMove(game, game.selected.r, game.selected.c, r, c, move.captured, false);
    }
    return { ...game, selected: null, validMoves: [] };
  }

  return game;
}

function applyMove(
  game: CheckersGame, sr: number, sc: number, dr: number, dc: number,
  captured: { r: number; c: number } | undefined, isMulti: boolean
): CheckersGame {
  const board = game.board.map((row) => [...row]);
  const p = board[sr][sc];
  board[sr][sc] = EMPTY;

  let newPiece = p;
  if (isRed(p) && dr === 0) newPiece = RED_KING;
  if (isBlack(p) && dr === SIZE - 1) newPiece = BLACK_KING;

  board[dr][dc] = newPiece;

  if (captured) {
    board[captured.r][captured.c] = EMPTY;
  }

  const isJump = !!captured;

  let nextTurn: 1 | 2 = game.turn === 1 ? 2 : 1;

  let multiJump: { r: number; c: number } | null = null;

  if (isJump) {
    const furtherJumps = hasJumpFrom(board, dr, dc);
    if (furtherJumps.length > 0) {
      multiJump = { r: dr, c: dc };
      nextTurn = game.turn;
    }
  }

  const allJumps = findAllJumps(board, nextTurn);
  const mustJump = allJumps.length > 0;
  const jumpPositions = allJumps;

  let selected: { r: number; c: number } | null = null;
  let validMoves: Array<{ r: number; c: number; jump: boolean; captured?: { r: number; c: number } }> = [];

  if (multiJump) {
    selected = { r: dr, c: dc };
    validMoves = hasJumpFrom(board, dr, dc).map((j) => ({
      r: j.r, c: j.c, jump: true, captured: j.captured,
    }));
  }

  let winner: 0 | 1 | 2 = 0;
  if (!hasPieces(board, 1)) winner = 2;
  if (!hasPieces(board, 2)) winner = 1;

  return {
    board, turn: nextTurn, winner, isDraw: false,
    mustJump, jumpPositions, selected, validMoves, multiJump,
  };
}

function hasPieces(board: Piece[][], player: 1 | 2): boolean {
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (p === EMPTY) continue;
      if (owner(p) === player) return true;
    }
  }
  return false;
}

function evaluateBoard(board: Piece[][], player: 1 | 2): number {
  let score = 0;
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (p === EMPTY) continue;
      const val = isKing(p) ? 3 : 1;
      const rowBonus = player === 1 ? (SIZE - 1 - r) : r;
      if (owner(p) === player) score += val + rowBonus * 0.1;
      else score -= val + rowBonus * 0.1;
    }
  }
  return score;
}

function cloneGame(g: CheckersGame): CheckersGame {
  return {
    ...g, board: g.board.map((row) => [...row]),
    jumpPositions: [...g.jumpPositions],
    validMoves: g.validMoves.map((m) => ({ ...m, captured: m.captured ? { ...m.captured } : undefined })),
    selected: g.selected ? { ...g.selected } : null,
    multiJump: g.multiJump ? { ...g.multiJump } : null,
  };
}

export function getAIMove(game: CheckersGame): { sr: number; sc: number; dr: number; dc: number } | null {
  const depth = 4;
  const allMoves = enumerateMoves(game);
  if (allMoves.length === 0) return null;

  let bestScore = -Infinity;
  let bestMove = allMoves[0];

  for (const move of allMoves) {
    const score = minimax(move.result, depth - 1, false, -Infinity, Infinity);
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return { sr: bestMove.sr, sc: bestMove.sc, dr: bestMove.dr, dc: bestMove.dc };
}

export function applyAIMove(game: CheckersGame, sr: number, sc: number, dr: number, dc: number): CheckersGame {
  if (game.multiJump) {
    return selectCell(game, dr, dc);
  }
  const withSelected = selectCell(game, sr, sc);
  if (!withSelected.selected) return game;
  return selectCell(withSelected, dr, dc);
}

function enumerateMoves(game: CheckersGame): Array<{ sr: number; sc: number; dr: number; dc: number; result: CheckersGame }> {
  const moves: Array<{ sr: number; sc: number; dr: number; dc: number; result: CheckersGame }> = [];
  const player = game.turn;

  if (game.multiJump) {
    const jumps = hasJumpFrom(game.board, game.multiJump.r, game.multiJump.c);
    for (const j of jumps) {
      const result = applyMove(cloneGame(game), game.multiJump.r, game.multiJump.c, j.r, j.c, j.captured, false);
      moves.push({ sr: game.multiJump.r, sc: game.multiJump.c, dr: j.r, dc: j.c, result });
    }
    return moves;
  }

  const allJumps = findAllJumps(game.board, player);
  if (allJumps.length > 0) {
    for (const pos of allJumps) {
      const jumps = hasJumpFrom(game.board, pos.r, pos.c);
      for (const j of jumps) {
        const result = applyMove(cloneGame(game), pos.r, pos.c, j.r, j.c, j.captured, false);
        moves.push({ sr: pos.r, sc: pos.c, dr: j.r, dc: j.c, result });
      }
    }
    return moves;
  }

  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const p = game.board[r][c];
      if (p === EMPTY || owner(p) !== player) continue;
      const simple = findSimpleMoves(game.board, r, c);
      for (const m of simple) {
        const g2 = cloneGame(game);
        const result = applyMove(g2, r, c, m.r, m.c, undefined, false);
        moves.push({ sr: r, sc: c, dr: m.r, dc: m.c, result });
      }
    }
  }
  return moves;
}

function minimax(game: CheckersGame, depth: number, maximizing: boolean, alpha: number, beta: number): number {
  if (depth === 0 || game.winner !== 0) {
    if (game.winner === 2) return 10000;
    if (game.winner === 1) return -10000;
    return evaluateBoard(game.board, 2);
  }

  const moves = enumerateMoves(game);
  if (moves.length === 0) {
    return maximizing ? -10000 : 10000;
  }

  if (maximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const ev = minimax(move.result, depth - 1, false, alpha, beta);
      maxEval = Math.max(maxEval, ev);
      alpha = Math.max(alpha, ev);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const ev = minimax(move.result, depth - 1, true, alpha, beta);
      minEval = Math.min(minEval, ev);
      beta = Math.min(beta, ev);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}
